import * as QRCode from "qrcode";
import { Logger } from "../logger/logger";

export class ColorQrCode {
	private static readonly QrCount: number = 6;

	public static async encodeMessageAsync(canvas: HTMLCanvasElement, message: string): Promise<boolean> {
		let codes = ColorQrCode.generateQrCodes(message);
		return await ColorQrCode.renderQrCodesAsync(canvas, codes);
	}

	private static generateQrCodes(message: string): QRCode.QRCode[] {
		let codes: QRCode.QRCode[] = [];
		let texts: string[] = [];

		let generateCode = (text: string, version?: number) => {
			let options: QRCode.QRCodeOptions = {
				version,
				errorCorrectionLevel: "low",
			};
			return QRCode.create(text, options);
		};

		for (let i = 0; i < ColorQrCode.QrCount; i++) {
			let start = i * Math.ceil(message.length / ColorQrCode.QrCount);
			let end = (i + 1) * Math.ceil(message.length / ColorQrCode.QrCount);
			if (i === ColorQrCode.QrCount - 1) {
				end = message.length;
			}

			let messagePart = message.substring(start, end);
			let qrCode = null;

			try {
				qrCode = generateCode(messagePart);
			} catch (e) {
				Logger.log("Failed to generate qr code", e);
				messagePart = "";
				qrCode = generateCode(messagePart);
			}

			texts.push(messagePart);
			codes.push(qrCode);
		}

		// NOTE(jpg): make sure all qr codes have the same version (size).
		let maxVersion = Math.max(...codes.map((code) => code.version));
		for (let i = 0; i < ColorQrCode.QrCount; i++) {
			if (codes[i].version !== maxVersion) {
				codes[i] = generateCode(texts[i], maxVersion);
			}
		}

		return codes;
	}

	private static async renderQrCodesAsync(canvas: HTMLCanvasElement, codes: QRCode.QRCode[]): Promise<boolean> {
		let context = canvas.getContext("2d");
		if (!context) {
			return false;
		}

		context.clearRect(0, 0, canvas.width, canvas.height);

		let imageData: ImageData;
		for (let i = 0; i < ColorQrCode.QrCount; i++) {
			let image = await new Promise<HTMLImageElement>((resolve) => {
				let newImage = new Image();
				newImage.onload = () => {
					resolve(newImage);
				};

				if (TextDecoder && ArrayBuffer) {
					for (let segment of codes[i].segments) {
						if (ArrayBuffer.isView(segment.data)) {
							segment.data = new TextDecoder().decode(segment.data);
						}
					}
				}

				QRCode.toDataURL(codes[i].segments).then((data) => {
					newImage.src = data;
				});
			});

			if (canvas.width < image.width || canvas.height < image.height) {
				canvas.width = Math.max(canvas.width, image.width);
				canvas.height = Math.max(canvas.height, image.height);
			}

			context.drawImage(image, 0, 0);

			let newImageData = context.getImageData(0, 0, canvas.width, canvas.height);
			if (!imageData) {
				imageData = newImageData;
			} else {
				let channel = Math.floor(i / 2);
				let mask = i % 2 === 0 ? 0b11111111 : 0b00001111;
				for (let j = channel; j < newImageData.data.length; j += 4) {
					// eslint-disable-next-line no-bitwise
					imageData.data[j] = (~mask & imageData.data[j]) | (mask & newImageData.data[j]); // tslint:disable-line no-bitwise
				}
			}
		}

		context.putImageData(imageData, 0, 0);
		return true;
	}
}